import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { useLeadSourcePickerOptions } from '@/hooks/filters/leadSourceOptions';
import { formatMoney } from '@/lib/reportFormat';
import { formatCount } from '@/lib/utils';
import * as crmSvc from '@/services/crm.service';
import type { SignUpsFigure } from '@/types/crm.types';

/**
 * The CRM dashboard's lead performance row (SCRUM-2178): New Plans by
 * Location, New Patients by Source and New Patient Sign-Ups.
 *
 * The figures come from GET /crm/lead-performance, which reads the same
 * service as the Leads standard reports, so a card and its workbook agree.
 * Each card follows the page's sidebar filters; New Patients also has its own
 * source choice (for example "only our online sources"), remembered per
 * browser.
 *
 * A figure the server could not work out arrives as null and is shown as a
 * failure — never as a zero, which would read as a real answer.
 */

/** Per-browser memory of the New Patients card's source choice. */
export const PICKED_SOURCES_STORAGE_KEY = 'crm-lead-performance-sources';

const LOAD_FAILED = "Couldn't load these numbers. Refresh the page to try again, and contact support if it keeps happening.";

function readPickedSources(): string[] {
  try {
    const raw = window.localStorage.getItem(PICKED_SOURCES_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function writePickedSources(values: string[]): void {
  try {
    window.localStorage.setItem(PICKED_SOURCES_STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Storage unavailable (private window, blocked site data): the choice
    // simply is not remembered.
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Oct 1 – Oct 13, 2026" from two Y-m-d strings. Split by hand rather than
 * through `new Date('YYYY-MM-DD')`, which parses as UTC midnight and lands on
 * the previous day in Pacific.
 */
export function formatRange(from: string, to: string): string {
  const parse = (value: string) => {
    const [y, m, d] = value.split('-').map(Number);
    return { y, label: `${MONTHS[(m || 1) - 1]} ${d}` };
  };
  const a = parse(from);
  const b = parse(to);

  return a.y === b.y ? `${a.label} – ${b.label}, ${b.y}` : `${a.label}, ${a.y} – ${b.label}, ${b.y}`;
}

function formatRate(signedUp: number, newPatients: number): string {
  return newPatients > 0 ? `${Math.round((signedUp / newPatients) * 100)}%` : '—';
}

function Message({ children }: { children: ReactNode }) {
  return <p className="py-6 text-sm text-slate-400">{children}</p>;
}

function Failed() {
  return (
    <div className="py-4">
      <p className="text-2xl font-semibold text-slate-400">—</p>
      <p className="mt-1 text-sm text-slate-500">{LOAD_FAILED}</p>
    </div>
  );
}

/** Loading, failed (null figure), empty, or the figure's own table. */
function FigureBody({
  loading,
  figure,
  empty,
  emptyText,
  children,
}: {
  loading: boolean;
  figure: unknown;
  empty: boolean;
  emptyText: string;
  children: () => ReactNode;
}) {
  if (loading) return <Message>Loading…</Message>;
  if (!figure) return <Failed />;
  if (empty) return <Message>{emptyText}</Message>;
  return <>{children()}</>;
}

// The cards are often a third of a narrow content area (the filter sidebar
// open on a laptop), so the number columns shrink to their contents and the
// location column takes whatever is left, truncating with its full name on
// hover. A percentage split starved the numbers and wrapped their headers.
const TABLE = 'w-full table-auto text-sm';
// The first header truncates like its cells: a one-line "Location / source"
// would otherwise set the column's width and push the rate out of the card.
const TH = 'w-full max-w-0 truncate py-1.5 text-left text-xs font-medium text-slate-500';
const TH_NUM = 'w-px whitespace-nowrap py-1.5 pl-3 text-right text-xs font-medium text-slate-500';
const TD = 'w-full max-w-0 truncate py-1.5 text-slate-900';
const TD_NUM = 'w-px whitespace-nowrap py-1.5 pl-3 text-right tabular-nums text-slate-900';
const TOTAL_ROW = 'border-t border-slate-200 font-semibold';

function FigureCard({
  id,
  title,
  range,
  action,
  children,
}: {
  id: string;
  title: string;
  range?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="border-0 shadow-sm" data-card={id}>
      <CardHeader className="pb-2 pt-4 px-5" action={action}>
        <span className="text-sm font-semibold">{title}</span>
        {range && <p className="text-xs text-muted-foreground">{range}</p>}
      </CardHeader>
      <CardBody className="px-5 pb-4">{children}</CardBody>
    </Card>
  );
}

function SignUpsTable({ figure, bySource }: { figure: SignUpsFigure; bySource: boolean }) {
  const sources = useMemo(() => {
    const grouped = new Map<number, SignUpsFigure['bySource']>();
    for (const row of figure.bySource) {
      grouped.set(row.facilityId, [...(grouped.get(row.facilityId) ?? []), row]);
    }
    return grouped;
  }, [figure.bySource]);

  return (
    <table className={TABLE}>
      <thead>
        <tr>
          <th className={TH}>{bySource ? 'Location / source' : 'Location'}</th>
          <th className={TH_NUM}>New</th>
          <th className={TH_NUM}>Signed</th>
          <th className={TH_NUM}>Rate</th>
        </tr>
      </thead>
      <tbody>
        {figure.rows.map((row) => (
          <Fragment key={row.facilityId}>
            <tr className={bySource ? 'font-medium' : undefined}>
              <td className={TD} title={row.facilityName}>{row.facilityName}</td>
              <td className={TD_NUM}>{formatCount(row.newPatients)}</td>
              <td className={TD_NUM}>{formatCount(row.signedUp)}</td>
              <td className={TD_NUM}>{formatRate(row.signedUp, row.newPatients)}</td>
            </tr>
            {bySource &&
              (sources.get(row.facilityId) ?? []).map((source) => (
                <tr key={`${row.facilityId}-${source.categoryId}`} className="text-slate-600">
                  <td className={`${TD} pl-3 text-slate-600`} title={source.categoryName}>
                    {source.categoryName}
                  </td>
                  <td className={TD_NUM}>{formatCount(source.newPatients)}</td>
                  <td className={TD_NUM}>{formatCount(source.signedUp)}</td>
                  <td className={TD_NUM}>{formatRate(source.signedUp, source.newPatients)}</td>
                </tr>
              ))}
          </Fragment>
        ))}
        <tr className={TOTAL_ROW}>
          <td className={TD}>All locations</td>
          <td className={TD_NUM}>{formatCount(figure.totalNewPatients)}</td>
          <td className={TD_NUM}>{formatCount(figure.totalSignedUp)}</td>
          <td className={TD_NUM}>{formatRate(figure.totalSignedUp, figure.totalNewPatients)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export function LeadPerformanceCards({
  filtersArray,
  enabled,
}: {
  /** The page's sidebar filters, as every other CRM dashboard query sends them. */
  filtersArray: string;
  /** False until the page's saved filters have loaded. */
  enabled: boolean;
}) {
  const [pickedSources, setPickedSources] = useState<string[]>(readPickedSources);
  const [bySource, setBySource] = useState(false);

  const sourceOptions = useLeadSourcePickerOptions();

  // A remembered choice can name sources this account does not have (the
  // browser is shared across accounts, or a source was removed). Once the
  // options are known, only the ones still offered are sent.
  const activeSources = useMemo(() => {
    if (sourceOptions.length === 0) return pickedSources;
    const offered = new Set(sourceOptions.map((o) => o.value));
    return pickedSources.filter((v) => offered.has(v));
  }, [pickedSources, sourceOptions]);

  const pickSources = (values: string[]) => {
    setPickedSources(values);
    writePickedSources(values);
  };

  const { data, isError, isPending } = useQuery({
    queryKey: ['crm-lead-performance', filtersArray, activeSources],
    queryFn: () => crmSvc.getCRMLeadPerformance({ filtersArray, pickedSources: activeSources }),
    staleTime: 60_000,
    enabled,
    placeholderData: keepPreviousData,
  });

  const loading = !isError && (isPending || !data);
  const newPlans = data?.newPlans ?? null;
  const newPatients = data?.newPatients ?? null;
  const signUps = data?.signUps ?? null;

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
      <div className="min-w-0">
        <FigureCard
          id="new-plans"
          title="New Plans by Location"
          range={newPlans ? formatRange(newPlans.dateFrom, newPlans.dateTo) : undefined}
        >
          <FigureBody
            loading={loading}
            figure={newPlans}
            empty={newPlans?.rows.length === 0}
            emptyText="No new plans in this range."
          >
            {() => newPlans && (
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH}>Location</th>
                  <th className={TH_NUM}>Patients</th>
                </tr>
              </thead>
              <tbody>
                {newPlans.rows.map((row) => (
                  <tr key={row.facilityId}>
                    <td className={TD} title={row.facilityName}>{row.facilityName}</td>
                    <td className={TD_NUM}>{formatCount(row.patients)}</td>
                  </tr>
                ))}
                <tr className={TOTAL_ROW}>
                  <td className={TD}>All locations</td>
                  <td className={TD_NUM}>{formatCount(newPlans.totalPatients)}</td>
                </tr>
              </tbody>
            </table>
            )}
          </FigureBody>
        </FigureCard>
      </div>

      <div className="min-w-0">
        <FigureCard
          id="new-patients"
          title="New Patients by Source"
          range={newPatients ? formatRange(newPatients.dateFrom, newPatients.dateTo) : undefined}
        >
          <div className="mb-3">
            <label htmlFor="crm-lead-performance-sources" className="sr-only">
              Lead sources
            </label>
            <SearchableMultiSelect
              id="crm-lead-performance-sources"
              options={sourceOptions}
              selectedValues={activeSources}
              onSelectionChange={pickSources}
              placeholder="All sources"
              searchPlaceholder="Search lead sources…"
              emptyMessage="No lead sources found"
            />
          </div>
          <FigureBody
            loading={loading}
            figure={newPatients}
            empty={newPatients?.rows.length === 0}
            emptyText="No new patients in this range."
          >
            {() => newPatients && (
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH}>Location</th>
                  <th className={TH_NUM}>New</th>
                  <th className={TH_NUM}>Collected</th>
                </tr>
              </thead>
              <tbody>
                {newPatients.rows.map((row) => (
                  <tr key={row.facilityId}>
                    <td className={TD} title={row.facilityName}>{row.facilityName}</td>
                    <td className={TD_NUM}>{formatCount(row.newPatients)}</td>
                    <td className={TD_NUM}>{formatMoney(row.revenue)}</td>
                  </tr>
                ))}
                <tr className={TOTAL_ROW}>
                  <td className={TD}>All locations</td>
                  <td className={TD_NUM}>{formatCount(newPatients.totalNewPatients)}</td>
                  <td className={TD_NUM}>{formatMoney(newPatients.totalRevenue)}</td>
                </tr>
              </tbody>
            </table>
            )}
          </FigureBody>
        </FigureCard>
      </div>

      <div className="min-w-0">
        <FigureCard
          id="sign-ups"
          title="New Patient Sign-Ups"
          range={signUps ? formatRange(signUps.dateFrom, signUps.dateTo) : undefined}
        >
          {signUps && signUps.rows.length > 0 && (
            <div className="mb-2 flex justify-end">
              <Button variant="link" onClick={() => setBySource((v) => !v)}>
                {bySource ? 'By location' : 'By source'}
              </Button>
            </div>
          )}
          <FigureBody
            loading={loading}
            figure={signUps}
            empty={signUps?.rows.length === 0}
            emptyText="No new patients in this range."
          >
            {() => signUps && <SignUpsTable figure={signUps} bySource={bySource} />}
          </FigureBody>
        </FigureCard>
      </div>
    </div>
  );
}
